"use client";

import type { ProcessedSet } from "@workspace/types";
import {
	isProgramSet,
	parseGenres,
	parseSpecialGuestsForBadges,
	setHasSpecialGuests,
} from "@workspace/types";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Label } from "@workspace/ui/components/label";
import { Textarea } from "@workspace/ui/components/textarea";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { ReviewLovedButton } from "@/components/features/festival/review/review-loved-button";
import { ReviewStars } from "@/components/features/festival/review/review-stars";
import { ReviewStatusButton } from "@/components/features/festival/review/review-status-button";
import { Facepile } from "@/components/shared/facepile";
import { GenreBadgeList } from "@/components/shared/genre-badge-list";
import { SetInfoDisplay } from "@/components/shared/set-info-display";
import { SpotifyPreviewPill } from "@/components/shared/spotify-preview-pill";
import { useRealtimeSetReview } from "@/hooks/features/festival/use-realtime-set-review";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";
import { isSetPast } from "@/lib/utils/date/formatting";
import { isEmptyReview } from "@workspace/shared-utils";
import { useCurrentUser } from "@/stores";

const extractYouTubeVideoId = (url: string): string | null => {
	const patterns = [
		/youtu\.be\/([a-zA-Z0-9_-]{11})/,
		/youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
		/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
		/youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
		/youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
	];

	for (const pattern of patterns) {
		const match = url.match(pattern);
		if (match?.[1]) return match[1];
	}
	return null;
};

interface FollowerInteraction {
	id: string;
	username: string | null;
	display_name: string;
	manual_avatar_url?: string | null;
	google_avatar_url?: string | null;
	rating?: number;
	loved?: boolean;
	must_see?: boolean;
	want_to_see?: boolean;
	saw_live?: boolean;
	saw_stream?: boolean;
	content?: string;
}

interface InitialReviewData {
	rating: number;
	content: string | null;
	loved: boolean;
	sawLive: boolean;
	sawStream: boolean;
	wantToSee: boolean;
	mustSee: boolean;
}

interface SetReviewModalProps {
	isOpen: boolean;
	set: ProcessedSet | undefined;
	festivalSlug?: string;
	onClose: () => void;
	onSaved?: () => void; // Optional callback after save
	socialInteractions?: FollowerInteraction[];
	initialReviewData?: InitialReviewData | null; // From parent's optimistic state
}

/**
 * Set Review Modal with Real-time Updates
 *
 * Features:
 * - Uses real-time subscription for instant sync across tabs/devices
 * - Handles both status toggles and full reviews
 * - Optimistic updates with ~100ms database confirmation
 * - Rich UI with genres, social interactions, and YouTube embeds
 */
export function SetReviewModal({
	isOpen,
	set,
	festivalSlug,
	onClose,
	onSaved,
	socialInteractions,
	initialReviewData,
}: SetReviewModalProps) {
	const currentUser = useCurrentUser();

	// Local state for form inputs
	const [localRating, setLocalRating] = useState(0);
	const [localContent, setLocalContent] = useState("");
	const [localLoved, setLocalLoved] = useState(false);
	const [localSawLive, setLocalSawLive] = useState(false);
	const [localSawStream, setLocalSawStream] = useState(false);
	const [localWantToSee, setLocalWantToSee] = useState(false);
	const [localMustSee, setLocalMustSee] = useState(false);

	// Use real-time hook for instant updates
	const { review, saveReview, isPending } = useRealtimeSetReview({
		setId: set?.id || "",
		artistId: set?.artist?.id || "",
		userId: currentUser?.id || null,
		enabled: !!currentUser?.id && !!set?.id && isOpen,
	});

	// Track if we had a review when the modal opened (for button text logic)
	const hadInitialReviewRef = useRef(false);
	// Track if form has been initialized for this modal open
	const formInitializedRef = useRef(false);

	// Initialize form ONCE when modal opens, using parent's optimistic data if available.
	// This prevents stale Supabase fetches from overwriting correct state.
	useEffect(() => {
		if (isOpen && !formInitializedRef.current) {
			// Prefer parent's optimistic state (always current), fall back to hook's review
			const source = initialReviewData ?? review;
			formInitializedRef.current = true;
			hadInitialReviewRef.current = !!source;

			if (source) {
				setLocalRating(source.rating || 0);
				setLocalContent(source.content || "");
				setLocalLoved(source.loved || false);
				setLocalSawLive(source.sawLive || false);
				setLocalSawStream(source.sawStream || false);
				setLocalWantToSee(source.wantToSee || false);
				setLocalMustSee(source.mustSee || false);
			} else {
				setLocalRating(0);
				setLocalContent("");
				setLocalLoved(false);
				setLocalSawLive(false);
				setLocalSawStream(false);
				setLocalWantToSee(false);
				setLocalMustSee(false);
			}
		} else if (!isOpen) {
			formInitializedRef.current = false;
		}
	}, [isOpen, initialReviewData, review]);

	// Handle save - matches original design where save handles both update and remove
	const handleSave = useCallback(async () => {
		if (!currentUser) return;

		await saveReview({
			rating: localRating,
			content: localContent,
			loved: localLoved,
			sawLive: localSawLive,
			sawStream: localSawStream,
			wantToSee: localWantToSee,
			mustSee: localMustSee,
		});

		onClose();
		onSaved?.();
	}, [
		currentUser,
		localRating,
		localContent,
		localLoved,
		localSawLive,
		localSawStream,
		localWantToSee,
		localMustSee,
		saveReview,
		onClose,
		onSaved,
	]);

	// Handle individual status button changes with proper exclusivity
	const handleStatusChange = useCallback(
		(status: string) => {
			if (status === "sawLive") {
				setLocalSawLive(!localSawLive);
				setLocalSawStream(false);
				setLocalWantToSee(false);
				setLocalMustSee(false);
			} else if (status === "sawStream") {
				setLocalSawStream(!localSawStream);
				setLocalSawLive(false);
				setLocalWantToSee(false);
				setLocalMustSee(false);
			} else if (status === "wantToSee") {
				setLocalWantToSee(!localWantToSee);
				setLocalMustSee(false);
				setLocalSawLive(false);
				setLocalSawStream(false);
			} else if (status === "mustSee") {
				setLocalMustSee(!localMustSee);
				setLocalWantToSee(false);
				setLocalSawLive(false);
				setLocalSawStream(false);
			}
		},
		[localSawLive, localSawStream, localWantToSee, localMustSee],
	);

	if (!set) return null;

	// Check if this is a program set
	const isProgram = isProgramSet(set);
	const isSetPastDate = isSetPast(set);
	const videoId = set.youtube_url
		? extractYouTubeVideoId(set.youtube_url)
		: null;

	// Create a review data object from local state to check if it's empty
	const localReviewData = {
		rating: localRating,
		content: localContent,
		loved: localLoved,
		sawLive: localSawLive,
		sawStream: localSawStream,
		wantToSee: localWantToSee,
		mustSee: localMustSee,
	};

	const isLocalReviewEmpty = isEmptyReview(localReviewData);

	// Prepare genres for display
	let genreArray: string[] = [];
	if (!isProgram) {
		if (set.is_collaborative && set.set_artists) {
			const allGenres = new Set<string>();
			for (const setArtist of set.set_artists) {
				if (setArtist.artist.spotify_genres) {
					const genres = parseGenres(
						setArtist.artist.spotify_genres,
						setArtist.artist.manual_genres,
					);
					for (const genre of genres) {
						allGenres.add(genre);
					}
				}
			}
			genreArray = Array.from(allGenres);
		} else if (set.artist?.spotify_genres) {
			genreArray = parseGenres(
				set.artist.spotify_genres,
				set.artist.manual_genres,
			);
		}
	}

	const hasGenres = genreArray.length > 0;
	// Filter out current user from social interactions - only show people you follow
	const followingInteractions = socialInteractions?.filter(
		(interaction) => interaction.id !== currentUser?.id
	) || [];
	const hasFollowing = followingInteractions.length > 0;

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="w-full max-w-[95%] p-0 sm:max-w-md">
				<DialogTitle className="sr-only">Review Set</DialogTitle>
				<DialogDescription className="sr-only">
					Rate and review this set. Share your experience and add personal notes
					about the performance.
				</DialogDescription>
				<DialogHeader className="space-y-4 px-6 pt-6 pb-0">
					{/* Artist photo and info */}
					<SetInfoDisplay
						set={set}
						context="modal"
						titleElement="h3"
						titleClassName="font-semibold text-base leading-tight"
						photoSize="lg"
						photoRounded="lg"
						showStage={true}
						showTime={true}
						showLiveIndicator={false}
						showGenres={false}
						photoType="artist"
					/>

					{/* Genres and Preview section */}
					{(hasGenres || (!isProgram && set.artist?.spotify_id)) && (
						<div className={hasGenres && !isProgram && set.artist?.spotify_id ? "grid grid-cols-2 gap-4" : ""}>
							{/* Genres */}
							{hasGenres && (
								<div className="space-y-1">
									<Label>Genres</Label>
									<GenreBadgeList genres={genreArray} className="gap-2" />
								</div>
							)}
							{/* Preview (hidden for group aliases since each member has their own on the detail page) */}
							{!isProgram && !set.is_group_alias && set.artist?.spotify_id && (
								<div className="space-y-1">
									<Label>Preview</Label>
									<SpotifyPreviewPill
										spotifyId={set.artist.spotify_id}
										artistName={set.artist.name}
									/>
								</div>
							)}
						</div>
					)}

					{/* Special Guests section */}
					{!isProgram && setHasSpecialGuests(set) && (
						<div className="space-y-1">
							<Label>Special Guests</Label>
							<div className="flex flex-wrap gap-1">
								{parseSpecialGuestsForBadges(set.special_guests || null).map(
									(guest, index) => (
										<Link
											key={index}
											href={`/artists/${createArtistSlug(guest)}`}
											className="cursor-pointer rounded-md bg-primary/10 px-2 py-1 font-medium text-primary text-xs transition-colors duration-200 hover:bg-primary/20"
										>
											{guest}
										</Link>
									),
								)}
							</div>
						</div>
					)}

					{/* Following section */}
					{hasFollowing && (
						<div className="space-y-1">
							<Label>People you follow</Label>
							<div>
								<Facepile
									interactions={followingInteractions}
									set={set}
									festivalSlug={festivalSlug}
									size="sm"
									allowHoverInModal={true}
								/>
							</div>
						</div>
					)}
				</DialogHeader>

				<div className="px-6">
					<div className="space-y-4">
						{/* YouTube Player */}
						{videoId && (
							<div className="overflow-hidden rounded-lg bg-black">
								<iframe
									src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
									width="100%"
									height="200"
									style={{ border: 0 }}
									allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
									allowFullScreen
									title={`${set.alias_name || set.artist?.name || "Unknown"} - Live Stream`}
									className="w-full"
								/>
							</div>
						)}

						<div className="relative flex items-center justify-center">
							<div className="flex items-center">
								<ReviewStars rating={localRating} onChange={setLocalRating} />

								<div className="mx-2 h-10 w-px bg-border/50 md:mx-4 md:h-14" />

								<ReviewLovedButton
									loved={localLoved}
									onChange={setLocalLoved}
								/>
							</div>
						</div>

						{/* Status buttons */}
						<div className="flex flex-wrap justify-center gap-1 text-sm sm:gap-2 md:gap-3">
							<ReviewStatusButton
								label={isSetPastDate ? "Wanted to See" : "Want to See"}
								icon="eye"
								isActive={localWantToSee}
								activeClassName="text-radix-plum-9"
								inactiveClassName="hover:bg-muted text-muted-foreground"
								onClick={() => handleStatusChange("wantToSee")}
							/>
							<ReviewStatusButton
								label="Must See"
								icon="flag"
								isActive={localMustSee}
								activeClassName="text-radix-violet-9"
								inactiveClassName="hover:bg-muted text-muted-foreground"
								onClick={() => handleStatusChange("mustSee")}
							/>
							<ReviewStatusButton
								label="Saw Live"
								icon="mic"
								isActive={localSawLive}
								activeClassName="text-radix-indigo-9"
								inactiveClassName="hover:bg-muted text-muted-foreground"
								onClick={() => handleStatusChange("sawLive")}
							/>
							<ReviewStatusButton
								label="Saw Stream"
								icon="live"
								isActive={localSawStream}
								activeClassName="text-radix-ruby-9"
								inactiveClassName="hover:bg-muted text-muted-foreground"
								onClick={() => handleStatusChange("sawStream")}
							/>
						</div>

						<Textarea
							placeholder={
								isProgram
									? "Write your thoughts about this program..."
									: "Write your review here (optional)..."
							}
							className="min-h-[100px]"
							value={localContent}
							onChange={(e) => setLocalContent(e.target.value)}
						/>
					</div>
				</div>

				<DialogFooter className="px-6 pt-0 pb-6">
					<div className="flex w-full items-center justify-between">
						{festivalSlug && set && (
							<Button variant="secondary" asChild>
								<Link href={`/festivals/${festivalSlug}/${set.id}`}>
									View Details
								</Link>
							</Button>
						)}
						{(!festivalSlug || !set) && <div />}
						<div className="flex gap-2">
							<Button
								variant="ghost"
								onClick={onClose}
								disabled={isPending}
								type="button"
							>
								Cancel
							</Button>
							<Button
								onClick={handleSave}
								disabled={isPending}
								variant={
									isLocalReviewEmpty && hadInitialReviewRef.current
										? "destructive"
										: "default"
								}
								type="button"
							>
								{(() => {
									const hadInitialReview = hadInitialReviewRef.current;

									// No initial review = creating new
									if (!hadInitialReview) {
										return isPending ? "Saving..." : "Save";
									}
									// Had initial review + cleared fields = removing
									if (isLocalReviewEmpty) {
										return isPending ? "Removing..." : "Remove";
									}
									// Had initial review + has content = updating
									return isPending ? "Updating..." : "Update";
								})()}
							</Button>
						</div>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
